import { AbsoluteTimeRange } from '@chronosphereio/core';
import { MarkAreaOption, MarkLineOption } from 'echarts/types/dist/shared';
import { useMemo } from 'react';
import { findTooltipEndTime, ShowEndTimeArgs } from '@/components';
import {
  EVENT_SERIES_NAME,
  EventPayload,
  EventSeriesOption,
  TimeScale,
  TimeSeriesDataset,
  TimeSeriesOption,
  TimeSeriesWithoutValues,
} from '@/core';
import { ClickSeriesData } from '../time-series-chart-model';

const SYMBOL_OFFSET = [0, -10]; // make sure event icon sits right above the grid

/**
 * Hook to create event series from EventEvents
 */
export function useComputeEventSeries({ events = [], startTime }: { events: EventPayload[]; startTime: number }) {
  return useMemo(() => {
    if (!events || events.length === 0) {
      return null;
    }

    // TODO: Don't hardcode this. Either make it configurable or compute it from the data.
    const defaultColor = '#8690B6';

    // Events with no end time are lines, events with end time are areas
    const eventLines: MarkLineOption['data'] = [];
    const eventAreas: MarkAreaOption['data'] = [];

    // use scatterplot to show icon for each event
    const eventDots: EventSeriesOption[] = [];

    for (const event of events) {
      const { timestampMs, endTimestampMs, icon, color } = event;
      const eventColor = color ?? defaultColor;

      if (endTimestampMs && endTimestampMs !== timestampMs) {
        eventAreas.push([
          {
            xAxis: timestampMs,
            itemStyle: {
              borderColor: eventColor,
              borderWidth: 1,
              color: eventColor,
              opacity: 0.1,
            },
          },
          { xAxis: endTimestampMs },
        ]);
        eventDots.push({
          symbol: icon,
          symbolOffset: SYMBOL_OFFSET,
          itemStyle: {
            color,
            opacity: 1,
          },
          value: [endTimestampMs, 1],
          event,
        });
      } else {
        eventLines.push({
          symbol: 'none',
          symbolSize: 0,
          itemStyle: {
            color: eventColor,
          },
          lineStyle: {
            type: 'solid',
          },
          xAxis: timestampMs,
        });
      }

      eventDots.push({
        symbol: icon,
        symbolOffset: SYMBOL_OFFSET,
        itemStyle: {
          color: eventColor,
          opacity: 1,
        },
        value: [timestampMs, 1],
        event,
      });
    }

    const lines = {
      name: 'Events',
      type: 'line',
      xAxisIndex: 0,
      data: [[startTime, null]],
      markArea: {
        data: eventAreas,
      },
      markLine: {
        label: {
          show: false,
        },
        symbol: 'none',
        lineStyle: {
          width: 2,
        },
        silent: true, // set it to true so markline label does not show on hover, but this disables all mouse events for the markline
        data: eventLines,
      },
    } as TimeSeriesOption;

    const scatter = {
      name: EVENT_SERIES_NAME,
      type: 'scatter',
      cursor: 'crosshair', // Hack to cover issue with cursor flipping from crosshair to pointer and back - potentially due to zoom being enabled by default
      yAxisIndex: 2,
      symbolSize: 20,
      color: defaultColor,
      data: eventDots,
    } as TimeSeriesOption;

    return [lines, scatter];
  }, [events, startTime]);
}

export const TIME_CHART_BREAKPOINT = {
  M: 600,
  S: 450,
  XS: 300,
} as const;

// Important this is ordered ascending for the `getMaxWidthBreakpoint` below to
// grab the right first value. Setting this once as a constant instead of inside
// the method to reduce the overhead.
const TIME_CHART_BREAKPOINT_MAX_WIDTHS = Object.values(TIME_CHART_BREAKPOINT).sort();

const minMs = 60000;
const hourMs = 3600000;
const dayMs = 86400000;

function getMaxWidthBreakpoint(width: number) {
  return TIME_CHART_BREAKPOINT_MAX_WIDTHS.find((breakpointWidth) => width <= breakpointWidth);
}

/**
 * Calculate the xAxis minimum interval based on the time range and the maximum width boundary.
 */
export function getXAxisMinInterval(width: number, rangeMs: number) {
  const maxWidth = getMaxWidthBreakpoint(width);

  // less than 300px
  if (maxWidth === TIME_CHART_BREAKPOINT.XS) {
    // between 15 and 30 mins
    if (minMs * 15 <= rangeMs && rangeMs < minMs * 30) {
      // minInterval will only work if the number is slightly greater than the desired value
      return minMs * 6; // ~5 mins
    }

    // between 30 mins and an hour
    if (minMs * 30 <= rangeMs && rangeMs < hourMs) {
      return minMs * 11; // ~10 mins
    }

    // between an hour and 6 hours
    if (hourMs <= rangeMs && rangeMs < hourMs * 6) {
      return minMs * 16; // ~15 mins
    }

    // between 6 and 12 hours
    if (hourMs * 6 <= rangeMs && rangeMs < hourMs * 12) {
      return hourMs * 2.1; // ~2 hours
    }

    // between 12 and 1 day
    if (hourMs * 12 <= rangeMs && rangeMs < dayMs) {
      return hourMs * 4.1; // ~4 hours
    }

    // greater than 1 day
    if (dayMs <= rangeMs) {
      return hourMs * 6.1; // ~6 hours
    }
  }

  // less than 450px
  if (maxWidth === TIME_CHART_BREAKPOINT.S) {
    // between 15 and 30 mins
    if (minMs * 15 <= rangeMs && rangeMs <= minMs * 30) {
      // minInterval will only work if the number is slightly greater than the desired value
      return minMs * 6; // ~5 mins
    }

    // between 30 mins and 2 hours
    if (minMs * 30 < rangeMs && rangeMs < hourMs * 2) {
      return minMs * 11; // ~10 mins
    }

    // between 2 hours and 6 hours
    if (hourMs * 2 <= rangeMs && rangeMs <= hourMs * 6) {
      return minMs * 31; // ~30 mins
    }

    // greater than 6 hours
    if (hourMs * 6 < rangeMs) {
      return hourMs * 2.1; // ~2 hours
    }
  }

  // less than 600px
  if (maxWidth === TIME_CHART_BREAKPOINT.M) {
    if (minMs * 15 <= rangeMs && rangeMs < hourMs) {
      return minMs * 6; // ~5 mins
    }

    // between an hour to 2 hours
    if (hourMs <= rangeMs && rangeMs < hourMs * 2) {
      return minMs * 11; // ~10 mins
    }

    // between 2 hours and 6 hours
    if (hourMs * 2 <= rangeMs && rangeMs <= hourMs * 6) {
      return minMs * 31; // ~30 mins
    }

    // greater than 6 hours
    if (hourMs * 6 < rangeMs) {
      return hourMs * 2.1; // ~2 hours
    }
  }
}

/**
 * Get the selected series given the click event
 */
interface GetClickSeriesData {
  seriesIndex?: number;
  dataIndex?: number;
  data?: unknown;
  series?: TimeSeriesWithoutValues;
  showTooltipEndTime?: ShowEndTimeArgs;
  timeChartData?: TimeSeriesDataset[];
  timeRange?: AbsoluteTimeRange;
  timeScale?: TimeScale;
}
export function getClickSeriesData({
  series,
  seriesIndex,
  dataIndex,
  data,
  showTooltipEndTime,
  timeChartData,
  timeRange,
  timeScale,
}: GetClickSeriesData) {
  if (!series) return; // return if no series is clicked

  let startMs;
  let endMs;
  let value;

  // Check if data is a time series value tuple
  // it could be undefined if no data point is clicked
  const isTimeSeriesValueTuple = Array.isArray(data) && data.length === 2;
  if (data && isTimeSeriesValueTuple) {
    startMs = data[0];
    // only return end time if showTooltipEndTime is defined
    endMs =
      showTooltipEndTime && timeChartData && timeRange && dataIndex !== undefined && seriesIndex !== undefined
        ? findTooltipEndTime({
            index: dataIndex,
            values: timeChartData[seriesIndex]?.source ?? [],
            timeRange,
            timeScale,
            showEndTime: showTooltipEndTime,
          })
        : undefined;
    value = data[1];
  }

  const seriesData: ClickSeriesData = {
    series,
    startMs,
    endMs,
    value,
  };

  return seriesData;
}
